import React, { useContext, useRef } from 'react';
import router from 'next/router';
import { isAfter, subMonths, parse } from 'date-fns';
import PropTypes from 'prop-types';
import { Elements } from '@stripe/react-stripe-js';
import { interpolate } from '@format/interpolate';
import { EmptyHeader } from '@web/molecules';
import { ServerSideErrorModal, NoRecipesFound } from '@web/components';
import {
  freshModalsContent,
  dryModalsContent,
} from '@web/testing/modalConstants';
import { PrefixedReporterProvider } from '../../reporter';
import {
  getNumStarterBoxWeeks,
  getStarterBoxDryConfig,
  getStarterBoxFreshConfig,
  getStarterBoxProductTypeConfig,
  getVariantsSortedByPrice,
  getVariantByRecipes,
  truncatePrice,
  getPricePropertyName,
  getStarterBoxProduct,
  createSkuWeeksMap,
  findProductByCadence,
} from '../../products';
import { getAge, getBreed, getActivity } from '../../pets';
import { ApiResult, useSession, wrapErrorWithResult } from '../../api';
import { CMSBlok } from '../../cms';
import { StripeContext } from '../../payment/stripe';
import { UserInfoForm } from '../UserInfoForm';
import { PupInfoForm } from '../PupInfoForm';
import { BirthdayForm } from '../BirthdayForm';
import { BreedForm } from '../BreedForm';
import { CaloriesForm } from '../CaloriesForm';
import { ActivityForm } from '../ActivityForm';
import { AllergiesForm } from '../AllergiesForm';
import { AboutForm } from '../AboutForm';
import { NutritionForm } from '../NutritionForm';
import {
  RecommendedMealPlanForm,
  RecommendedPlanContainer,
} from '../RecommendedPlanForm';
import { PlanSelectionContainer } from '../PlanSelectionContainer';
import { PlanTypeSelectionForm } from '../PlanTypeSelectionForm';
import { FreshRecipesSelectionForm } from '../FreshRecipesSelectionForm';
import { DryRecipesSelectionForm } from '../DryRecipesSelectionForm';
import { AddOnSelectionForm } from '../AddOnSelectionForm';
import { CheckoutContainer } from '../CheckoutContainer';
import { CheckoutConfirmationContainer } from '../CheckoutConfirmationContainer';
import { AppConfirmationContainer } from '../CheckoutConfirmationContainer/AppConfirmationContainer';
import planHeaderPhoto from '../../images/product-photos/pack-shots/pack-on-board-with-shadow.png';
import { QuizHeader } from '../QuizHeader';
import { HappinessQuizHeader } from '../HappinessQuizHeader';
import { useOffer } from '../../offer';
import { PoopForm } from '../PoopForm';
import { OnboardingInterstitial } from '../OnboardingInterstitial';
import { ALLERGIES_SLUG } from '../routing';
import {
  aboutFormProps,
  nutritionFormProps,
  recipes as freshRecipes,
  dryRecipes,
  CRO_ONBOARDING_INTERSTITIAL_PROPS,
  mealPlanModalContent,
} from '../constants';
import {
  userSkipsAddonSelection,
  userContinuesWithAddonSelection,
} from './events';
import {
  STATE,
  ACTIONS,
  levelToLabel,
  DENTAL_CHEWS_SKUS,
  ONBOARDING_VARIATIONS,
  MEAL_PREP_CRO_DATA,
  RECOMMENDED_PLAN_CRO_DATA,
  HAPPINESS_QUIZ_STEPS,
} from './constants';
import { Main, Wrapper } from './styles';
import { OnboardingContext } from './context';
import { interpolations } from './interpolation';
import {
  getPrices,
  getHighestDailyCalories,
  onCartSubmission,
  getLowestCostVariant,
  showNoRecipeFound,
} from './functions';
import { isCompletePet } from './guard';

const QuizContainer = ({
  stepNumber,
  children,
  steps = 4,
  variation = 'control',
  discountBannerBgColor = 'status.successPrimary',
  discountBannerFontColor = 'text.lightest',
  discountBannerCopy,
}) => {
  const {
    offer,
    showModal,
    setShowModal,
    flags: { showDiscountBanner },
  } = useContext(OnboardingContext);

  return (
    <Wrapper variation={variation}>
      {variation === 'happiness' ? (
        <HappinessQuizHeader
          currentStep={stepNumber}
          steps={HAPPINESS_QUIZ_STEPS}
          pillText={offer.formattedAmount + ' off | Happiness Guaranteed'}
        />
      ) : (
        <QuizHeader
          discountBannerBGColor={discountBannerBgColor}
          discountBannerFontColor={discountBannerFontColor}
          discountBannerCopy={discountBannerCopy}
          showPromoBar={showDiscountBanner}
          currentStep={stepNumber}
          totalSteps={steps}
        />
      )}

      <Main>{children}</Main>
      <ServerSideErrorModal
        shown={showModal}
        headerText="Hmm, something's not right."
        errorMessage="We weren't able to complete your request, please refresh the page and try again."
        onCloseModal={() => setShowModal(false)}
      />
    </Wrapper>
  );
};

QuizContainer.propTypes = {
  steps: PropTypes.number,
  stepNumber: PropTypes.number.isRequired,
  children: PropTypes.object.isRequired,
  discountBannerCopy: PropTypes.string,
  discountBannerBgColor: PropTypes.string,
  discountBannerFontColor: PropTypes.string,
};

const UserInfo = props => {
  const {
    customer,
    pet,
    send,
    getCustomer,
    updateCustomer,
    goToLogin,
    reporter,
    setShowModal,
    petsApi,
    cartApi,
    quizStepNumbers,
    savePet,
    goBack,
    deleteLocalPet,
    setReturningCustomerWithMealPlan,
    isReturningCustomerWithMealPlan,
    flags: { happinessOnboardingQuiz },
  } = useContext(OnboardingContext);
  const { service: sessionService } = useSession();

  const defaultValues = {
    firstName: customer.first_name || '',
    email: customer.email || '',
  };

  const saveCustomer = async ({ firstName, email }) => {
    if (customer.id) {
      try {
        const response = await updateCustomer({
          ...(firstName ? { first_name: firstName } : {}),
          email,
        });

        return {
          action: response.status === 201 ? 'create' : 'update',
          customer: response.data,
        };
      } catch (err) {
        const result = wrapErrorWithResult(err);
        if (result.error.type !== 'emailConflict') {
          throw err;
        }
      }
    }
    await sessionService.register(firstName, email);
    const dataResponse = await getCustomer(sessionService?.user?.id);
    return {
      action: sessionService.user.is_new ? 'create' : 'update',
      customer: dataResponse.data,
    };
  };

  const onSubmit = async formData => {
    let result;
    try {
      let payload = await saveCustomer(formData);
      if (payload.action === 'update') {
        const petResponse = await savePet(pet);
        const listPetResponse = await petsApi.listPets();
        const cartResponse = await cartApi.refreshCart();
        const lineItems = cartResponse.payload.line_items;
        let pets;
        if (!!listPetResponse.data.results.length) {
          pets = listPetResponse.data.results.filter(pet => {
            if (pet.id === petResponse.data.id) return true;
            const lineItem = lineItems.find(
              lineItem => lineItem.pet_id === pet.id,
            );
            return pet.id === lineItem?.pet_id;
          });
        }
        const petWithSub = cartResponse.payload?.line_items.filter(
          ({ subscription }) => subscription?.sku !== '',
        );
        setReturningCustomerWithMealPlan(
          isReturningCustomerWithMealPlan === null && petWithSub?.length > 0
            ? true
            : false,
        );
        payload = {
          ...payload,
          inProcessPet: petResponse.data,
          pets: pets,
          cart: cartResponse.payload,
        };
      } else {
        setReturningCustomerWithMealPlan(false);
        const petResponse = await savePet(pet);
        const cartResponse = await cartApi.refreshCart();
        payload = {
          ...payload,
          pet: petResponse.data,
          cart: cartResponse.payload,
        };
      }

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        deleteLocalPet?.();
        const petsWhenLocal = payload.pet ? [payload.pet] : [];
        send(
          ACTIONS.customerInfoSaved({
            customer: payload.customer,
            // payload.inProcessPet is present on 'update', payload.pet is present on 'create'
            pet: payload.inProcessPet ?? payload.pet ?? {},
            pets: payload.pets ?? petsWhenLocal,
            cart: payload.cart ?? null,
          }),
        );
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 0,
  };

  return (
    <QuizContainer
      discountBannerCopy={props.discountBannerCopy}
      discountBannerBgColor={props.discountBannerBgColor}
      discountBannerFontColor={props.discountBannerFontColor}
      stepNumber={
        happinessOnboardingQuiz === 'happiness'
          ? HAPPINESS_QUIZ_STEPS[3]
          : quizStepNumbers.userInfo
      }
      variation={happinessOnboardingQuiz}
    >
      <UserInfoForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        petName={pet?.name}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const PupInfo = props => {
  const {
    pet,
    savePet,
    send,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    flags: { shortenedOnboardingQuiz, happinessOnboardingQuiz },
  } = useContext(OnboardingContext);

  const defaultValues = {
    gender: pet.gender,
    name: pet.name,
  };

  const onSubmit = async ({ gender, name }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ gender, name });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        if (shortenedOnboardingQuiz === ONBOARDING_VARIATIONS.control) {
          send(ACTIONS.toBirthday({ pet: payload.pet }));
        } else {
          send(ACTIONS.toCalories({ pet: payload.pet }));
        }
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 1,
  };
  const goBack = () => router.push('/');

  return (
    <QuizContainer
      discountBannerCopy={props.discountBannerCopy}
      discountBannerBgColor={props.discountBannerBgColor}
      discountBannerFontColor={props.discountBannerFontColor}
      stepNumber={
        happinessOnboardingQuiz === 'happiness'
          ? HAPPINESS_QUIZ_STEPS[0]
          : quizStepNumbers.pupInfo
      }
      variation={happinessOnboardingQuiz}
    >
      <PupInfoForm
        {...cmsProps}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
      />
    </QuizContainer>
  );
};

const Birthday = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    flags: { shortenedOnboardingQuiz },
  } = useContext(OnboardingContext);

  const [year, month, day] = pet.birth_date?.split('-') ?? ['', '', ''];
  const defaultValues = {
    year: year,
    month: month,
    day: day,
  };

  const onSubmit = async ({ year, month, day }) => {
    const birthDate = `${year}-${month}-${+day || 1}`;
    let result, payload;
    try {
      const response = await savePet({ birth_date: birthDate });
      payload = {
        action: 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        if (shortenedOnboardingQuiz === ONBOARDING_VARIATIONS.control) {
          send(ACTIONS.toBreed({ pet: payload.pet }));
        } else {
          send(ACTIONS.toCalories({ pet: payload.pet }));
        }
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 2,
  };

  return (
    <QuizContainer
      stepNumber={quizStepNumbers.birthday}
      variation={shortenedOnboardingQuiz}
    >
      <BirthdayForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Breed = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    isLocalPet,
  } = useContext(OnboardingContext);

  const defaultValues = {
    breeds:
      pet.breeds?.map(breed => ({
        value: breed.id,
        label: breed.name,
      })) ?? [],
  };

  const onSubmit = async ({ breeds }) => {
    const newBreeds = breeds.map(breed =>
      isLocalPet ? { id: breed.value, name: breed.label } : breed.value,
    );
    let result, payload;
    try {
      const response = await savePet({ breeds: newBreeds });
      payload = {
        action: 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toCalories({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 3,
  };

  return (
    <QuizContainer stepNumber={quizStepNumbers.breeds}>
      <BreedForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Calories = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    flags: { happinessOnboardingQuiz },
  } = useContext(OnboardingContext);

  const defaultValues = {
    weight: pet.weight,
    idealWeight: pet.ideal_weight,
    spayedStatus: pet.is_neutered,
  };

  const onSubmit = async ({ weight, idealWeight, spayedStatus }) => {
    let result, payload;
    try {
      const response = await savePet({
        ideal_weight: idealWeight,
        weight,
        is_neutered: spayedStatus,
      });
      payload = {
        action: 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }
    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toActivity({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 4,
  };

  const isPuppy = isAfter(
    parse(pet.birth_date, 'yyyy-MM-dd', new Date()),
    subMonths(Date.now(), 12),
  );

  return (
    <QuizContainer
      discountBannerCopy={props.discountBannerCopy}
      discountBannerBgColor={props.discountBannerBgColor}
      discountBannerFontColor={props.discountBannerFontColor}
      stepNumber={
        happinessOnboardingQuiz === 'happiness'
          ? HAPPINESS_QUIZ_STEPS[1]
          : quizStepNumbers.calories
      }
      variation={happinessOnboardingQuiz}
    >
      <CaloriesForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
        isPuppy={isPuppy}
      />
    </QuizContainer>
  );
};

const Activity = props => {
  const {
    pet,
    customer,
    pets,
    cart,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    flags: { happinessOnboardingQuiz, showOnboardingInterstitial },
  } = useContext(OnboardingContext);

  const defaultValues = {
    activity: pet.activity_level,
  };
  const petsRef = useRef(pets);
  petsRef.current = pets;
  const onSubmit = async ({ activity }) => {
    let result, payload;
    try {
      const response = await savePet({ activity_level: activity });
      payload = {
        action: 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }
    ApiResult.match(result, {
      success: async payload => {
        if (showOnboardingInterstitial) {
          send(ACTIONS.toWeightInterstitial({ pet: payload.pet }));
        } else {
          if (!customer.id) {
            send(ACTIONS.toUserInfo({ pet: payload.pet }));
          } else {
            send(
              ACTIONS.toRecommendedPlan({
                customer,
                pet: payload.pet,
                pets: petsRef.current,
                cart,
              }),
            );
          }
        }
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 5,
  };
  return (
    <QuizContainer
      discountBannerCopy={props.discountBannerCopy}
      discountBannerBgColor={props.discountBannerBgColor}
      discountBannerFontColor={props.discountBannerFontColor}
      stepNumber={
        happinessOnboardingQuiz === 'happiness'
          ? HAPPINESS_QUIZ_STEPS[2]
          : quizStepNumbers.activity
      }
      variation={happinessOnboardingQuiz}
    >
      <ActivityForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Allergies = props => {
  const {
    customer,
    pet,
    savePet,
    cart,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    send,
    petsApi,
    isLocalPet,
    quizStepNumbers,
    flags: { shortenedOnboardingQuiz },
  } = useContext(OnboardingContext);

  const defaultValues = {
    allergies:
      pet.allergies?.map(({ id, name }) => ({
        label: name,
        value: id,
      })) ?? [],
  };

  const onSubmit = async ({ allergies }) => {
    const allergyIds = allergies.map(({ value, label }) =>
      isLocalPet ? { id: value, name: label } : value,
    );
    let result, payload;
    try {
      const response = await savePet({
        allergies: allergyIds,
      });

      let petResponse;
      // Pet 2 and beyond will not be local
      if (!isLocalPet) petResponse = await petsApi.listPets();
      // First pet will be local
      else petResponse = { data: [response.data] };

      payload = {
        action: 'update',
        pet: response.data,
        pets: petResponse?.data.results,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        if (shortenedOnboardingQuiz === ONBOARDING_VARIATIONS.control) {
          send(
            ACTIONS.toPoop({
              customer,
              pet: payload.pet,
              cart,
              ...(payload.pets && { pets: payload.pets }),
            }),
          );
        } else {
          send(ACTIONS.toUserInfo({ pet: payload.pet }));
        }
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 6,
  };

  return (
    <QuizContainer
      stepNumber={quizStepNumbers.allergies}
      variation={shortenedOnboardingQuiz}
    >
      <AllergiesForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Poop = props => {
  const {
    pet,
    savePet,
    send,
    cart,
    customer,
    goBack,
    goToLogin,
    pets,
    reporter,
    setShowModal,
    quizStepNumbers,
  } = useContext(OnboardingContext);

  const defaultValues = {
    stoolType: pet.stool_type ?? null,
  };

  const onSubmit = async ({ stoolType }) => {
    let result, payload;
    try {
      const response = await savePet({ stool_type: stoolType });
      payload = {
        action: 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(
          ACTIONS.toRecommendedPlan({
            customer,
            pets,
            pet: payload.pet,
            cart,
          }),
        );
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 7,
  };

  return (
    <QuizContainer stepNumber={quizStepNumbers.poop}>
      <PoopForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const PlanSelection = ({ planSelectionInfo, body, noRecipesInfo }) => {
  const {
    productTypes,
    selectedPlanType,
    cartProduct,
    pet,
    returnToQuiz,
    goBack,
    send,
    service,
    pets,
    onClickDeletePet,
    pricePeriod,
    isAppSession,
    flags: { defaultCadenceOnboarding },
  } = useContext(OnboardingContext);

  const showRemovePetButton = pets?.length > 1;
  const onDeletePet = () => {
    onClickDeletePet(pet.id);
  };
  if (showNoRecipeFound(productTypes)) {
    return (
      <Wrapper>
        <EmptyHeader />
        <NoRecipesFound
          {...interpolate(noRecipesInfo, {
            pupName: pet?.name,
          })}
          allergies={pet?.allergies.map(({ name }) => ({
            label: name,
          }))}
          onClick={() => returnToQuiz(ALLERGIES_SLUG)}
          onClickDeletePet={showRemovePetButton ? onDeletePet : null}
        />
        {body?.map(content => (
          <CMSBlok key={content._uid} content={content} />
        ))}
      </Wrapper>
    );
  }

  const { defaultProductType, unavailableProductTypes, subscriptionProducts } =
    getStarterBoxProductTypeConfig(productTypes, cartProduct, selectedPlanType);

  const lowestMixedVariant = !!productTypes.mixed.starterBoxes.length
    ? getLowestCostVariant(productTypes.mixed.starterBoxes[0].variants)
    : null;
  let lowestFreshVariant = !!productTypes.fresh.starterBoxes.length
    ? getLowestCostVariant(productTypes.fresh.starterBoxes[0].variants)
    : null;
  const lowestHalfFreshVariant = !!productTypes.half_fresh.starterBoxes.length
    ? getLowestCostVariant(productTypes.half_fresh.starterBoxes[0].variants)
    : null;
  const lowestBakedVariant = !!productTypes.dry.starterBoxes.length
    ? getLowestCostVariant(productTypes.dry.starterBoxes[0].variants)
    : null;

  if (defaultCadenceOnboarding) {
    const skuWithCadence = createSkuWeeksMap(productTypes['fresh']);
    const defaultCadence = skuWithCadence[lowestFreshVariant.subscription_sku];
    if (defaultCadence === 8) {
      const newCadence = 4;
      const newProduct = findProductByCadence(
        productTypes['fresh'],
        newCadence,
      );
      if (newProduct) {
        const newVariants = newProduct.variants;
        lowestFreshVariant = getLowestCostVariant(newVariants) ?? null;
      }
    }
  }

  const pricePerPeriodIndex = getPricePropertyName(pricePeriod);
  const discountedPricePerPeriodIndex = getPricePropertyName(pricePeriod, {
    discounted: true,
  });

  const productTypeInterpolationSubstitutes = {
    pupName: pet.name,
    mixedPricePerWeek: lowestMixedVariant?.[pricePerPeriodIndex],
    freshPricePerWeek: lowestFreshVariant?.[pricePerPeriodIndex],
    halfFreshPricePerWeek: lowestHalfFreshVariant?.[pricePerPeriodIndex],
    bakedPricePerWeek: lowestBakedVariant?.[pricePerPeriodIndex],
    mixedDiscountPricePerWeek:
      lowestMixedVariant?.[discountedPricePerPeriodIndex],
    freshDiscountPricePerWeek:
      lowestFreshVariant?.[discountedPricePerPeriodIndex],
    halfFreshDiscountPricePerWeek:
      lowestHalfFreshVariant?.[discountedPricePerPeriodIndex],
    bakedDiscountPricePerWeek:
      lowestBakedVariant?.[discountedPricePerPeriodIndex],
  };

  const onSubmit = async planType => {
    send(ACTIONS.selectedPlanType({ planType }));
    return onCartSubmission(service);
  };
  body = MEAL_PREP_CRO_DATA.body;
  planSelectionInfo = MEAL_PREP_CRO_DATA.planSelectionInfo;

  return (
    <PlanSelectionContainer body={body}>
      <PlanTypeSelectionForm
        {...interpolate(planSelectionInfo, {
          pupName: pet.name,
          pupWeight: pet.weight,
          pupActivity: getActivity(pet.activity_level),
          pupCalories: parseInt(
            getHighestDailyCalories(subscriptionProducts),
            10,
          ),
        })}
        planTypes={interpolate(
          planSelectionInfo.items,
          productTypeInterpolationSubstitutes,
        )}
        collapsedPlanTypes={interpolate(
          planSelectionInfo.collapsedItems,
          productTypeInterpolationSubstitutes,
        )}
        defaultPlanType={defaultProductType}
        unavailablePlanTypes={unavailableProductTypes}
        returnToQuiz={returnToQuiz}
        goBack={isAppSession ? null : goBack}
        onSubmit={onSubmit}
        period={pricePeriod}
      />
    </PlanSelectionContainer>
  );
};

PlanSelection.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  body: PropTypes.array.isRequired,
  noRecipesInfo: PropTypes.object,
};

const RecommendedPlan = ({ planSelectionInfo, body, noRecipesInfo }) => {
  const {
    setShowModal,
    reporter,
    productTypes,
    cartProduct,
    setLocalSelection,
    offer,
    pet,
    pet_id: petId,
    cartApi,
    send,
    service,
    pets,
    onClickDeletePet,
    returnToQuiz,
    pricePeriod,
    goBack,
    isAppSession,
  } = useContext(OnboardingContext);

  const showRemovePetButton = pets?.length > 1;

  const onDeletePet = () => {
    onClickDeletePet(pet.id);
  };

  if (showNoRecipeFound(productTypes)) {
    return (
      <Wrapper>
        <EmptyHeader />
        <NoRecipesFound
          {...interpolate(noRecipesInfo, {
            pupName: pet?.name,
          })}
          allergies={pet?.allergies.map(({ name }) => ({
            label: name,
          }))}
          onClick={() => returnToQuiz(ALLERGIES_SLUG)}
          onClickDeletePet={showRemovePetButton ? onDeletePet : null}
        />
        {body?.map(content => (
          <CMSBlok key={content._uid} content={content} />
        ))}
      </Wrapper>
    );
  }

  const getConfigFromRecipes = (
    selectedMealPrepType,
    selectedFreshRecipes,
    selectedBakedRecipes,
  ) => {
    if (
      selectedMealPrepType === 'fresh' ||
      selectedMealPrepType === 'half_fresh'
    ) {
      const product = getStarterBoxProduct(productTypes[selectedMealPrepType]);

      if (!selectedFreshRecipes.length)
        return { message: 'Choose one or more recipes.' };

      const variants = getVariantsSortedByPrice(product.variants);

      const newVariant = getVariantByRecipes(variants, {
        fresh: selectedFreshRecipes,
        dry: product.variants[0].recipes.dry,
      });
      const { price, discountedPrice } = getPrices(
        offer,
        newVariant,
        pricePeriod,
      );

      return {
        price,
        discountedPrice,
      };
    } else {
      const product = getStarterBoxProduct(productTypes['mixed']);

      if (!selectedFreshRecipes.length)
        return { message: 'Choose one or more fresh recipes.' };

      if (!selectedBakedRecipes.length)
        return { message: 'Choose one or more baked recipes.' };

      const variants = getVariantsSortedByPrice(product.variants);
      const newVariant = getVariantByRecipes(variants, {
        fresh: selectedFreshRecipes,
        dry: selectedBakedRecipes,
      });
      const { price, discountedPrice } = getPrices(
        offer,
        newVariant,
        pricePeriod,
      );
      return {
        price,
        discountedPrice,
      };
    }
  };

  const lowestMixedVariant = !!productTypes.mixed.starterBoxes.length
    ? getLowestCostVariant(productTypes.mixed.starterBoxes[0].variants)
    : null;
  let lowestFreshVariant = !!productTypes.fresh.starterBoxes.length
    ? getLowestCostVariant(productTypes.fresh.starterBoxes[0].variants)
    : null;
  const lowestHalfFreshVariant = !!productTypes.half_fresh.starterBoxes.length
    ? getLowestCostVariant(productTypes.half_fresh.starterBoxes[0].variants)
    : null;
  const lowestBakedVariant = !!productTypes.dry.starterBoxes.length
    ? getLowestCostVariant(productTypes.dry.starterBoxes[0].variants)
    : null;

  const pricePerPeriodIndex = getPricePropertyName(pricePeriod);
  const discountedPricePerPeriodIndex = getPricePropertyName(pricePeriod, {
    discounted: true,
  });

  const productTypeInterpolationSubstitutes = {
    mixedPricePerWeek: lowestMixedVariant?.[pricePerPeriodIndex],
    freshPricePerWeek: lowestFreshVariant?.[pricePerPeriodIndex],
    halfFreshPricePerWeek: lowestHalfFreshVariant?.[pricePerPeriodIndex],
    bakedPricePerWeek: lowestBakedVariant?.[pricePerPeriodIndex],
    mixedDiscountPricePerWeek:
      lowestMixedVariant?.[discountedPricePerPeriodIndex],
    freshDiscountPricePerWeek:
      lowestFreshVariant?.[discountedPricePerPeriodIndex],
    halfFreshDiscountPricePerWeek:
      lowestHalfFreshVariant?.[discountedPricePerPeriodIndex],
    bakedDiscountPricePerWeek:
      lowestBakedVariant?.[discountedPricePerPeriodIndex],
  };

  const { defaultProductType, unavailableProductTypes } =
    getStarterBoxProductTypeConfig(productTypes, cartProduct);

  const headline = interpolate('Recommended for {{pupName}}', {
    pupName: pet.name,
  });

  const subheadline = interpolate(
    "As a {{pupWeight}}-pound {{pupActivity}} pup, we've created meals perfect for {{pupName}}'s complete nutrition needs.",
    {
      pupName: pet.name,
      pupWeight: pet.weight,
      pupActivity: levelToLabel[pet.activity_level],
    },
  );

  const {
    defaultRecipes: defaultFreshRecipes,
    unavailableRecipes: unavailableFreshRecipes,
  } = getStarterBoxFreshConfig(productTypes['fresh'], cartProduct, null);

  const {
    defaultRecipes: defaultBakedRecipes,
    unavailableRecipes: unavailableBakedRecipes,
  } = getStarterBoxDryConfig(productTypes['dry'], cartProduct, null);

  const onMealPlanAndRecipesSubmit = async (
    planType,
    freshRecipes,
    bakedRecipes,
  ) => {
    send(ACTIONS.selectedPlanType({ planType: planType }));
    if (planType === 'mixed') {
      send(
        ACTIONS.selectedMixedRecipes(
          { recipes: { dry: bakedRecipes, fresh: freshRecipes } },
          {
            productTypes,
            cartProduct,
            cartApi,
            reporter,
            petId,
            setShowModal,
            setLocalSelection,
          },
        ),
      );
    } else {
      send(
        ACTIONS.selectedFreshRecipes(
          { recipes: freshRecipes },
          {
            productTypes,
            cartProduct,
            cartApi,
            reporter,
            petId,
            setShowModal,
            setLocalSelection,
          },
        ),
      );
    }
    return onCartSubmission(service);
  };
  const modals = [...freshModalsContent, ...dryModalsContent];
  //* END FRESH RECOMMENDATION PAGE DATA

  return (
    <RecommendedPlanContainer>
      <RecommendedMealPlanForm
        onSubmit={onMealPlanAndRecipesSubmit}
        headline={headline}
        subheadline={subheadline}
        ctaText={`Continue with ${offer.formattedAmount} off`}
        modals={modals}
        freshRecipes={freshRecipes}
        defaultFreshRecipes={defaultFreshRecipes}
        bakedRecipes={dryRecipes}
        defaultBakedRecipes={defaultBakedRecipes}
        defaultMealPrepType={defaultProductType}
        mealPrepTypes={interpolate(
          MEAL_PREP_CRO_DATA.planSelectionInfoRecommendedPage.items,
          productTypeInterpolationSubstitutes,
        )}
        collapsedMealPrepTypes={interpolate(
          MEAL_PREP_CRO_DATA.planSelectionInfoRecommendedPage.collapsedItems,
          productTypeInterpolationSubstitutes,
        )}
        unavailableMealPrepTypes={unavailableProductTypes}
        unavailableFreshRecipes={unavailableFreshRecipes}
        unavailableBakedRecipes={unavailableBakedRecipes}
        onBackButtonClick={isAppSession ? null : goBack}
        period={pricePeriod}
        getConfigFromRecipes={getConfigFromRecipes}
        splitScreenPromoProps={RECOMMENDED_PLAN_CRO_DATA}
        headerImage={planHeaderPhoto}
        modalContent={interpolate(
          mealPlanModalContent,
          productTypeInterpolationSubstitutes,
        )}
        notSelectableText={`Based on your answers, this meal plan is unavailable for ${pet.name}.`}
      />
    </RecommendedPlanContainer>
  );
};

RecommendedPlan.propTypes = {
  body: PropTypes.array,
  noRecipesInfo: PropTypes.object,
};

const Fresh = ({ planSelectionInfo, body, noRecipesInfo }) => {
  const {
    reporter,
    productTypes,
    selectedPlanType,
    cartProduct,
    setShowModal,
    setLocalSelection,
    offer,
    pet,
    pet_id: petId,
    cartApi,
    send,
    service,
    pets,
    onClickDeletePet,
    returnToQuiz,
    pricePeriod,
    goBack,
    flags: { defaultCadenceOnboarding },
  } = useContext(OnboardingContext);

  const showRemovePetButton = pets?.length > 1;
  const onDeletePet = () => {
    onClickDeletePet(pet.id);
  };

  if (showNoRecipeFound(productTypes)) {
    return (
      <Wrapper>
        <EmptyHeader />
        <NoRecipesFound
          {...interpolate(noRecipesInfo, {
            pupName: pet?.name,
          })}
          allergies={pet?.allergies.map(({ name }) => ({
            label: name,
          }))}
          onClick={returnToQuiz}
          onClickDeletePet={showRemovePetButton ? onDeletePet : null}
        />
        {body?.map(content => (
          <CMSBlok key={content._uid} content={content} />
        ))}
      </Wrapper>
    );
  }

  const { defaultRecipes, product, unavailableRecipes } =
    getStarterBoxFreshConfig(productTypes[selectedPlanType], cartProduct, null);
  const onSubmit = async freshRecipes => {
    send(
      ACTIONS.selectedFreshRecipes(
        { recipes: freshRecipes },
        {
          productTypes,
          cartProduct,
          cartApi,
          reporter,
          petId,
          setShowModal,
          setLocalSelection,
        },
      ),
    );
    return onCartSubmission(service);
  };

  let recommendedProduct = product;

  if (defaultCadenceOnboarding && selectedPlanType === 'fresh') {
    const skuWithCadence = createSkuWeeksMap(productTypes['fresh']);

    const defaultCadence = skuWithCadence[product.variants[0].subscription_sku];
    if (defaultCadence === 8) {
      const newCadence = 4;
      const newProduct = findProductByCadence(
        productTypes['fresh'],
        newCadence,
      );
      if (newProduct) {
        recommendedProduct = newProduct;
      }
    }
  }

  const getConfigFromRecipes = selectedRecipes => {
    if (!selectedRecipes.length)
      return { message: 'Choose one or more recipes.' };

    const variants = getVariantsSortedByPrice(recommendedProduct.variants);

    const newVariant = getVariantByRecipes(variants, {
      fresh: selectedRecipes,
      dry: recommendedProduct.variants[0].recipes.dry,
    });
    const { price, discountedPrice } = getPrices(
      offer,
      newVariant,
      pricePeriod,
    );

    return {
      price,
      discountedPrice,
    };
  };

  const checkoutBarHeadline =
    pet.name.toUpperCase() +
    "'S " +
    product.weeks_of_food +
    '-WEEK STARTER BOX';
  return (
    <PlanSelectionContainer body={body}>
      <FreshRecipesSelectionForm
        {...interpolate(planSelectionInfo, {
          pupName: pet.name,
          pupWeight: pet.weight,
          pupAge: getAge(pet.birth_date),
          pupActivity: levelToLabel[pet.activity_level],
          pupBreed: getBreed(pet.breeds),
          pupAllergies: pet.allergies,
          numStarterBoxWeeks: getNumStarterBoxWeeks(
            productTypes[selectedPlanType],
          ),
        })}
        checkoutBarHeadline={checkoutBarHeadline}
        defaultRecipes={defaultRecipes}
        onSubmit={onSubmit}
        onBackButtonClick={goBack}
        unavailableRecipes={unavailableRecipes}
        getConfigFromRecipes={getConfigFromRecipes}
        period={pricePeriod}
      />
    </PlanSelectionContainer>
  );
};

Fresh.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  body: PropTypes.array.isRequired,
  noRecipesInfo: PropTypes.object,
};

const Baked = ({ planSelectionInfo, body }) => {
  const {
    reporter,
    productTypes,
    selectedPlanType,
    selectedFreshRecipes,
    cartProduct,
    setShowModal,
    setLocalSelection,
    offer,
    pet,
    pet_id: petId,
    cartApi,
    send,
    service,
    pricePeriod,
    goBack,
  } = useContext(OnboardingContext);

  const { defaultRecipes, product, unavailableRecipes } =
    getStarterBoxDryConfig(productTypes[selectedPlanType], cartProduct);

  const onSubmit = async dryRecipes => {
    send(
      ACTIONS.selectedBakedRecipes(
        { recipes: dryRecipes },
        {
          productTypes,
          cartProduct,
          cartApi,
          reporter,
          petId,
          setShowModal,
          setLocalSelection,
        },
      ),
    );

    return onCartSubmission(service);
  };

  const getConfigFromRecipes = selectedRecipes => {
    if (!selectedRecipes.length)
      return { message: 'Choose one or more recipes.' };
    const variants = getVariantsSortedByPrice(product.variants);
    const newVariant = getVariantByRecipes(variants, {
      dry: selectedRecipes,
      fresh: selectedFreshRecipes,
    });

    const { price, discountedPrice } = getPrices(
      offer,
      newVariant,
      pricePeriod,
    );

    return {
      price,
      discountedPrice,
    };
  };

  const checkoutBarHeadline =
    pet.name.toUpperCase() +
    "'S " +
    product.weeks_of_food +
    '-WEEK STARTER BOX';

  return (
    <PlanSelectionContainer body={body}>
      <DryRecipesSelectionForm
        {...interpolate(planSelectionInfo, {
          pupName: pet.name,
          pupWeight: pet.weight,
          pupAge: getAge(pet.birth_date),
          pupActivity: levelToLabel[pet.activity_level],
          pupBreed: getBreed(pet.breeds),
          pupAllergies: pet.allergies,
          numStarterBoxWeeks: getNumStarterBoxWeeks(
            productTypes[selectedPlanType],
          ),
        })}
        checkoutBarHeadline={checkoutBarHeadline}
        defaultRecipes={defaultRecipes}
        onSubmit={onSubmit}
        onBackButtonClick={goBack}
        unavailableRecipes={unavailableRecipes}
        getConfigFromRecipes={getConfigFromRecipes}
        period={pricePeriod}
      />
    </PlanSelectionContainer>
  );
};

Baked.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  body: PropTypes.array.isRequired,
};

const Addons = ({
  headline,
  subheadline,
  body,
  addonSections,
  modals,
  desktopCtaText,
}) => {
  const {
    reporter,
    goBack,
    setShowModal,
    pet,
    pet_id: petId,
    cartApi,
    send,
    selectedAddons: defaultAddons,
    treatProducts,
    supplementProducts,
    flags: { extrasPageCRO },
  } = useContext(OnboardingContext);
  const offer = useOffer();

  const filteredTreatProducts = treatProducts.filter(
    treat =>
      !DENTAL_CHEWS_SKUS.includes(treat.sku) || treat.product_recommended,
  );

  const findProductBySku = (products, sku) => {
    return products.find(product => product.sku === sku);
  };

  const mergedAddonSection = addonSections.map((section, i) => {
    const addons = section.addons
      .map(addon => {
        const products =
          addon.productType === 'TR'
            ? filteredTreatProducts
            : supplementProducts;

        const product = findProductBySku(products, addon.sku);

        if (!product) return null;

        return interpolate(addon, {
          addonPrice: truncatePrice(product.price),
          addonDiscount: truncatePrice(product.discounted_price),
          petName: pet.name,
          addonSavePercentage: offer.formattedAmount,
        });
      })
      .filter(Boolean);

    if (extrasPageCRO === 'best_sellers') {
      if (i === 0) {
        section.heading = 'Best Sellers';
      } else {
        section.heading = '';
      }
    }

    return {
      ...section,
      addons,
    };
  });

  const mergedModals = modals
    .map(modal => {
      const product = findProductBySku(
        [...supplementProducts, ...filteredTreatProducts],
        modal.sku,
      );

      if (!product) return null;

      return interpolate(modal, {
        addonPrice: truncatePrice(product.price),
        addonDiscount: truncatePrice(product.discounted_price),
        addonSavePercentage: offer.formattedAmount,
      });
    })
    .filter(Boolean);

  const unavailableAddons = [
    ...filteredTreatProducts
      .filter(treat => !treat.product_available)
      .map(treat => treat.sku),
    ...supplementProducts
      .filter(supplement => !supplement.product_available)
      .map(supplement => supplement.sku),
  ];

  const onSkip = async () => {
    const addonsToDelete = defaultAddons.filter(addon =>
      unavailableAddons.find(
        unavailableAddon => unavailableAddon === addon.sku,
      ),
    );

    let result = ApiResult.success();
    if (addonsToDelete.length > 0) {
      result = await ApiResult.flatMapAsync(result, () =>
        cartApi.removeFromCart({
          addons: addonsToDelete,
        }),
      );
    }

    ApiResult.match(result, {
      success: () => {
        reporter.tag(userSkipsAddonSelection());
        send(ACTIONS.toCheckout());
      },
      error: {
        client: error => {
          reporter.error(error.original);
          setShowModal(true);
        },
        server: () => setShowModal(true),
      },
    });
  };

  const onSubmit = async selectedAddons => {
    const addonsToDelete = defaultAddons.filter(
      addon =>
        !selectedAddons.find(
          selectedAddon => selectedAddon.sku === addon.sku,
        ) ||
        unavailableAddons.find(
          unavailableAddon => unavailableAddon === addon.sku,
        ),
    );

    let result = ApiResult.success();
    if (addonsToDelete.length > 0) {
      result = await ApiResult.flatMapAsync(result, () =>
        cartApi.removeFromCart({
          addons: addonsToDelete,
        }),
      );
    }
    if (selectedAddons.length > 0) {
      result = await ApiResult.flatMapAsync(result, () =>
        cartApi.addAddonsToCart({
          pet_id: petId,
          addons: selectedAddons.map(addon => ({
            sku: addon.sku,
            frequency: addon.frequency,
            quantity: addon.quantity,
            recurrence: addon.recurrence,
          })),
        }),
      );
    }

    ApiResult.match(result, {
      success: () => {
        reporter.tag(userContinuesWithAddonSelection(selectedAddons));
        send(ACTIONS.toCheckout());
      },
      error: {
        client: error => {
          reporter.error(error.original);
          setShowModal(true);
        },
        server: () => setShowModal(true),
      },
    });

    return result;
  };

  if (extrasPageCRO === 'best_sellers') {
    subheadline =
      'Select up to three (3) healthy treats and supplements to compliment our core food plans';
  }

  return (
    <PlanSelectionContainer body={body}>
      <AddOnSelectionForm
        headline={interpolate(headline, { pupName: pet.name })}
        subheadline={subheadline}
        addonSections={mergedAddonSection}
        defaultAddons={defaultAddons}
        unavailableAddons={unavailableAddons}
        onSubmit={onSubmit}
        onSkipButtonClick={onSkip}
        onBackButtonClick={goBack}
        submitButtonText={desktopCtaText}
        modals={mergedModals}
      />
    </PlanSelectionContainer>
  );
};

Addons.propTypes = {
  headline: PropTypes.string,
  subheadline: PropTypes.string,
  body: PropTypes.array,
  addonSections: PropTypes.array,
  desktopCtaText: PropTypes.string,
  modals: PropTypes.array,
};

const Checkout = props => {
  const { clientSecret, stripePromise, isFreeOrder, clientSetupSecret } =
    useContext(StripeContext);

  const appearance = {
    theme: 'stripe',
    variables: {
      colorBackground: '#F4F4EC',
    },
    rules: {
      '.Input': {
        backgroundColor: '#FFF',
      },
      '.AccordionItem': {
        border: 'none',
        boxShadow: 'none',
      },
    },
  };
  const checkoutOptions = {
    clientSecret: isFreeOrder ? clientSetupSecret : clientSecret,
    appearance,
    fonts: [
      {
        // Local fonts will not load properly without https in development
        // src URLs need to start with 'https://' or 'data:'
        family: 'Founders Grotesk',
        src: `url(/fonts/founders-grotesk-web-regular.woff2) format('woff')`,
      },
    ],
  };

  return (
    <PrefixedReporterProvider prefix="Checkout">
      {clientSecret && (
        <Elements
          key={isFreeOrder ? clientSetupSecret : clientSecret}
          stripe={stripePromise}
          options={checkoutOptions}
        >
          <CheckoutContainer {...props} />
        </Elements>
      )}
    </PrefixedReporterProvider>
  );
};

const Confirmation = props => {
  return (
    <PrefixedReporterProvider prefix="Confirmation">
      <CheckoutConfirmationContainer {...props} />
    </PrefixedReporterProvider>
  );
};
const AppConfirmation = props => {
  return (
    <PrefixedReporterProvider prefix="AppConfirmation">
      <AppConfirmationContainer {...props} />
    </PrefixedReporterProvider>
  );
};

/* Shortened Onboarding Flow Components*/
const About = () => {
  const {
    pet,
    savePet,
    send,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    isLocalPet,
  } = useContext(OnboardingContext);
  const [year, month, day] = pet.birth_date?.split('-') ?? ['', '', ''];
  const defaultValues = {
    defaultValuesGender: {
      gender: pet.gender,
      name: pet.name,
    },
    defaultValuesBirthday: {
      year: year,
      month: month,
      day: day,
    },
    defaultValuesBreeds: {
      breeds:
        pet.breeds?.map(breed => ({
          value: breed.id,
          label: breed.name,
        })) ?? [],
    },
    defaultValuesCalories: {
      weight: pet.weight,
      idealWeight: pet.ideal_weight,
      spayedStatus: pet.is_neutered,
    },
  };

  const onSubmit = async ({
    gender,
    name,
    year,
    month,
    day,
    breeds,
    weight,
    idealWeight,
    spayedStatus,
  }) => {
    const birthDate = `${year}-${month}-${+day || 1}`;
    const newBreeds =
      breeds?.map(breed =>
        isLocalPet ? { id: breed.value, name: breed.label } : breed.value,
      ) || [];
    let result, payload;
    try {
      const response = await savePet({
        gender,
        name,
        birth_date: birthDate,
        breeds: newBreeds,
        ideal_weight: idealWeight,
        weight,
        is_neutered: spayedStatus,
      });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toNutrition({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const goBack = () => router.push('/');

  return (
    <QuizContainer stepNumber={quizStepNumbers.about} steps={2}>
      <AboutForm
        {...aboutFormProps}
        {...defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
      />
    </QuizContainer>
  );
};
const Nutrition = () => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    customer,
    getCustomer,
    updateCustomer,
    isLocalPet,
    cartApi,
    petsApi,
    deleteLocalPet,
  } = useContext(OnboardingContext);
  const { service: sessionService } = useSession();
  const defaultValues = {
    activity: pet.activity_level ?? null,
    allergies:
      pet.allergies?.map(({ id, name }) => ({
        label: name,
        value: id,
      })) ?? [],
    stoolType: pet.stool_type ?? null,
    firstName: customer.first_name || '',
    email: customer.email || '',
  };
  const poopPrompt = `What is ${pet.name}'s typical stool situation?`;

  const saveCustomer = async ({ firstName, email }) => {
    if (customer.id) {
      try {
        const response = await updateCustomer({
          first_name: firstName,
          email,
        });

        return {
          action: response.status === 201 ? 'create' : 'update',
          customer: response.data,
        };
      } catch (err) {
        const result = wrapErrorWithResult(err);
        if (result.error.type !== 'emailConflict') {
          throw err;
        }
      }
    }
    await sessionService.register(firstName, email);
    const dataResponse = await getCustomer(sessionService?.user?.id);
    return {
      action: sessionService.user.is_new ? 'create' : 'update',
      customer: dataResponse.data,
    };
  };

  const onSubmit = async ({
    activity,
    stoolType,
    allergies,
    firstName,
    email,
  }) => {
    const allergyIds = allergies.map(({ value, label }) =>
      isLocalPet ? { id: value, name: label } : value,
    );
    let result;
    try {
      const userData = { firstName, email };
      let payload = await saveCustomer(userData);

      // handles returning customer that has cleared cookies
      if (payload.action === 'update') {
        const petResponse = await petsApi.listPets();
        let inProcessPet, pets;
        if (!!petResponse.data.results.length) {
          pets = petResponse.data.results;
          inProcessPet =
            petResponse.data.results.find(pet => !isCompletePet(pet)) ??
            petResponse.data.results[0];
        }
        const cartResponse = await cartApi.refreshCart();
        payload = {
          ...payload,
          inProcessPet: inProcessPet,
          pets: pets,
          cart: cartResponse.payload,
        };
      } else {
        const petResponse = await savePet({
          ...pet,
          activity_level: activity,
          stool_type: stoolType,
          allergies: allergyIds,
        });
        const cartResponse = await cartApi.refreshCart();
        payload = {
          ...payload,
          pet: petResponse.data,
          cart: cartResponse.payload,
        };
      }

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        deleteLocalPet?.();
        const petsWhenLocal = payload.pet ? [payload.pet] : [];
        send(
          ACTIONS.customerInfoSaved({
            customer: payload.customer,
            // payload.inProcessPet is present on 'update', payload.pet is present on 'create'
            pet: payload.inProcessPet ?? payload.pet ?? {},
            pets: payload.pets ?? petsWhenLocal,
            cart: payload.cart ?? null,
          }),
        );
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  return (
    <QuizContainer stepNumber={quizStepNumbers.nutrition} steps={2}>
      <NutritionForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        poopQuestionPrompt={poopPrompt}
        {...nutritionFormProps}
      />
    </QuizContainer>
  );
};

const WeightInterstitial = () => {
  const {
    pet,
    send,
    quizStepNumbers,
    goBack,
    customer,
    pets,
    cart,
    flags: { shortenedOnboardingQuiz },
  } = useContext(OnboardingContext);

  const onSubmit = () => {
    if (!customer.id) {
      send(ACTIONS.toUserInfo({ pet: pet }));
    } else {
      send(
        ACTIONS.toRecommendedPlan({
          customer,
          pet: pet,
          pets: pets,
          cart,
        }),
      );
    }
  };

  return (
    <QuizContainer
      stepNumber={quizStepNumbers.activity}
      variation={shortenedOnboardingQuiz}
    >
      <OnboardingInterstitial
        {...CRO_ONBOARDING_INTERSTITIAL_PROPS}
        body={`We’ve helped over 840,000 dogs like ${pet.name} find the right portion and maintain a healthy weight.`}
        goBack={goBack}
        onSubmit={onSubmit}
        previousQuestion="activity_question"
      />
    </QuizContainer>
  );
};

export const components = {
  [STATE.userInfo]: UserInfo,
  [STATE.pupInfo]: PupInfo,
  [STATE.birthday]: Birthday,
  [STATE.breed]: Breed,
  [STATE.calories]: Calories,
  [STATE.activity]: Activity,
  [STATE.allergies]: Allergies,
  [STATE.poop]: Poop,
  [STATE.recommendedPlan]: RecommendedPlan,
  [STATE.planSelection]: PlanSelection,
  [STATE.fresh]: Fresh,
  [STATE.baked]: Baked,
  [STATE.addons]: Addons,
  [STATE.checkout]: Checkout,
  [STATE.confirmation]: Confirmation,
  [STATE.appConfirmation]: AppConfirmation,
  [STATE.about]: About,
  [STATE.nutrition]: Nutrition,
  [STATE.weightInterstitial]: WeightInterstitial,
};
